<template>
  <div id="landing-app">
    <ThreeBackground />
    <StepLayout
      :current-step="currentStep"
      :total-steps="totalSteps"
      :form-data="formData"
      :validation-message="validationMessage"
      @next="nextStep"
      @prev="prevStep"
      @go-to="goToStep"
      @submit="submitForm"
      @update-form="updateForm"
    />
  </div>
</template>

<script>
import ThreeBackground from './components/ThreeBackground.vue'
import StepLayout from './components/StepLayout.vue'
import { submit } from './services/submitForm'
import { validateStep } from './utils/validateStep'

export default {
  name: 'App',
  components: { ThreeBackground, StepLayout },
  data() {
    return {
      currentStep: 0,
      totalSteps: 10,
      submitting: false,
      validationKey: '',
      formData: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        company: '',
        position: '',
        hotelCount: 1,
        hotels: [{ street: '', houseNumber: '', zip: '', city: '', description: '', units: 1 }],
        channelManager: '',
        hasReception: null,
        hasStripe: null,
        hasPhoneSystem: null,
        wantsKiosk: null,
        // "yyyy-MM-dd HH:mm" slot for the first phone meeting, or 'SKIP'
        meetingSlot: '',
      },
    }
  },
  watch: {
    '$route.params.lang'(lang) {
      if (lang && ['de', 'en'].includes(lang)) {
        this.$i18n.locale = lang
      }
    },
  },
  created() {
    const lang = this.$route.params.lang
    if (lang && ['de', 'en'].includes(lang)) {
      this.$i18n.locale = lang
    }
  },
  computed: {
    validationMessage() {
      return this.validationKey ? this.$t(this.validationKey) : ''
    },
  },
  methods: {
    updateForm(payload) {
      this.validationKey = ''
      Object.keys(payload).forEach(key => {
        this.$set(this.formData, key, payload[key])
      })
    },
    nextStep() {
      if (this.currentStep === 0) {
        if (this.currentStep < this.totalSteps) this.currentStep++
        return
      }
      const result = validateStep(this.currentStep, this.formData)
      if (!result.valid) {
        this.validationKey = result.messageKey || 'validation.required'
        return
      }
      this.validationKey = ''
      if (this.currentStep < this.totalSteps) {
        this.currentStep++
      }
    },
    prevStep() {
      this.validationKey = ''
      if (this.currentStep > 0) {
        this.currentStep--
      }
    },
    goToStep(step) {
      if (step >= 0 && step <= this.totalSteps) {
        this.currentStep = step
      }
    },
    async submitForm() {
      if (this.submitting) return
      const result = validateStep(this.totalSteps - 1, this.formData)
      if (!result.valid) {
        this.validationKey = result.messageKey || 'validation.required'
        return
      }
      this.validationKey = ''
      this.submitting = true
      try {
        await submit(this.formData, this.$i18n.locale)
        this.currentStep = this.totalSteps
      } catch (e) {
        console.error('Submit failed', e)
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style lang="scss">
:root {
  --gold: #D0B092;
  --gold-dark: #C9A78A;
  --gold-light: #E8D5C0;
  --navy: #1a1a2e;
  --navy-light: #16213e;
  --white: #ffffff;
  --text: #2d2d3f;
  --text-muted: #8e8ea0;
  --accent: #7367f0;
  --font: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

*, *::before, *::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  width: 100%;
  height: 100%;
  overflow: hidden;
  font-family: var(--font);
  background: var(--navy);
  color: var(--white);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#landing-app {
  width: 100%;
  height: 100vh;
  height: 100dvh;
  position: relative;
  overflow: hidden;
}

input, select, textarea, button {
  font-family: var(--font);
}

::selection {
  background: var(--gold);
  color: var(--navy);
}
</style>
